import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLang, t } from '../i18n';
import Header from '../components/Header';
import { useOrder } from '../OrderContext';
import { fetchMachineProducts, type MachineProduct } from '../api';
import { startChatSession, sendChatMessage, endChatSession, ChatApiError } from './chat/api';
import type { OrderSuggestion } from './chat/types';

const SESSION_TIMEOUT_MS = 3 * 60 * 1000;

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  orderSuggestion?: OrderSuggestion | null;
}

let messageSeq = 0;
const nextId = () => `m${++messageSeq}`;

const ChatPage = () => {
  const navigate = useNavigate();
  const { order, resetOrder, updateOrder } = useOrder();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Expiry timer ───────────────────────────────────────────────────────────
  const resetExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    expiryTimerRef.current = setTimeout(() => setSessionExpired(true), SESSION_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  // ── Scroll to bottom on new content ────────────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isThinking]);

  // ── Session start ──────────────────────────────────────────────────────────
  useEffect(() => {
    const user = order.user ?? { name: t.chatGuestName, age: 25, gender: 'other' };

    fetchMachineProducts()
      .then((products) => {
        const enabled = products.filter((p) => p.is_enabled && p.quantity_ml > 0);
        // Localized names so the greeting and order card show the guest's
        // language. Machine product_ids are UUIDs, so match by backend name.
        const localizedItem = (p: MachineProduct) => ({
          id: p.product_id,
          name: t.machineProductNames[p.name] ?? t.productNames[p.product_id] ?? p.name,
          desc: t.productDescs[p.product_id] ?? '',
        });
        const menu = enabled.map(localizedItem);
        const mixing_capabilities = {
          total_volume_ml: 200,
          ingredients: enabled.map(localizedItem),
          preset_recipes: [] as { id: string; name: string; mix: Record<string, number> }[],
        };
        return startChatSession({ user, menu, mixing_capabilities, lang: getLang() });
      })
      .then((res) => {
        setSessionId(res.session_id);
        setMessages([{ id: nextId(), role: 'assistant', text: res.greeting, orderSuggestion: res.order_suggestion }]);
        resetExpiryTimer();
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setSessionError(msg);
        setMessages([{ id: nextId(), role: 'assistant', text: t.chatWelcome }]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Send message ───────────────────────────────────────────────────────────
  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || isThinking) return;

      setMessages((prev) => [...prev, { id: nextId(), role: 'user', text }]);
      setInput('');
      setIsThinking(true);

      try {
        if (!sessionId) throw new Error('Chat session not ready.');
        const res = await sendChatMessage({ session_id: sessionId, message: text });
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'assistant', text: res.reply, orderSuggestion: res.order_suggestion },
        ]);
        resetExpiryTimer();
      } catch (err) {
        if (err instanceof ChatApiError) {
          if (err.status === 410 || err.status === 404) {
            setSessionExpired(true);
            return;
          }
          if (err.status === 429) {
            setMessages((prev) => [
              ...prev,
              { id: nextId(), role: 'assistant', text: t.chatRateLimited },
            ]);
            return;
          }
        }
        setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', text: t.chatError }]);
      } finally {
        setIsThinking(false);
        inputRef.current?.focus();
      }
    },
    [sessionId, isThinking, resetExpiryTimer],
  );

  // ── Order ──────────────────────────────────────────────────────────────────
  const handleOrder = (suggestion: OrderSuggestion) => {
    const user = order.user;

    // Fire-and-forget — don't block navigation on it
    if (sessionId) void endChatSession({ session_id: sessionId }).catch(() => {});

    resetOrder();

    if (suggestion.kind === 'preset') {
      updateOrder({
        orderMode: 'main',
        user,
        product: { id: suggestion.product_id, icon: '', name: suggestion.name, desc: '', basePrice: 0 },
        customMix: { [suggestion.product_id]: 200 },
        totalPrice: 0,
      });
    } else {
      updateOrder({
        orderMode: 'custom',
        user,
        product: { id: 'custom', icon: '⚗️', name: suggestion.name, desc: '', basePrice: 0 },
        customMix: suggestion.mix,
        totalPrice: 0,
      });
    }

    navigate('/qr-order');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void send(input);
  };

  const showSuggestions = messages.length <= 1 && !isThinking;
  const inputDisabled = isThinking || (!sessionId && !sessionError) || sessionExpired;

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.aiPickChat} onBack={() => navigate('/ai-pick-menu')} />

      {/* Message list */}
      <div
        ref={scrollRef}
        className="relative z-10 flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 sm:px-5"
      >
        {messages.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <span className="text-[0.9rem] text-[var(--muted)]">{t.chatConnecting}</span>
          </div>
        )}

        {messages.map((m) => (
          <React.Fragment key={m.id}>
            <MessageBubble message={m} />
            {m.orderSuggestion && (
              <OrderCard
                suggestion={m.orderSuggestion}
                onOrder={() => handleOrder(m.orderSuggestion!)}
              />
            )}
          </React.Fragment>
        ))}

        {isThinking && <TypingIndicator />}

        {showSuggestions && messages.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-2 pt-3">
            {t.chatSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void send(s)}
                className="touch-target rounded-full border border-[var(--border)] bg-[rgba(255,255,255,0.04)] px-4 py-2 text-[0.85rem] font-medium text-[var(--muted-lt)] transition-all duration-150 active:scale-[0.96] active:border-[var(--gold-lt)] active:text-[var(--cream)]"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className="relative z-10 flex shrink-0 items-center gap-2.5 border-t border-[var(--border)] px-3 py-3 sm:px-5"
        style={{ background: 'rgba(10,10,10,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={inputDisabled ? t.chatConnecting : t.chatPlaceholder}
          disabled={inputDisabled}
          enterKeyHint="send"
          className="min-w-0 flex-1 rounded-full border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-5 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none transition-colors focus:border-[var(--gold-lt)] disabled:opacity-40"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={!input.trim() || inputDisabled}
          className="touch-target flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[var(--confirm-border)] bg-[var(--confirm-bg)] text-[var(--gold-lt)] shadow-[0_0_18px_var(--confirm-shadow)] transition-all duration-150 active:scale-[0.92] disabled:opacity-35 disabled:shadow-none"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M22 2 11 13" />
            <path d="M22 2 15 22l-4-9-9-4 20-7Z" />
          </svg>
        </button>
      </form>

      {/* Session expired overlay */}
      {sessionExpired && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-5 px-8"
          style={{ background: 'rgba(8,8,8,0.88)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
        >
          <div className="text-[2.5rem]">⏱</div>
          <div className="text-center">
            <p className="font-display text-[1.3rem] font-bold text-[var(--cream)]">{t.chatSessionExpiredTitle}</p>
            <p className="mt-1 text-[0.9rem] text-[var(--muted)]">{t.chatSessionExpiredBody}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/ai-pick-menu')}
            className="touch-target rounded-[14px] border-2 border-[var(--confirm-border)] bg-[var(--confirm-bg)] px-8 py-4 font-display text-[1rem] font-bold uppercase tracking-[0.12em] text-white shadow-[0_0_18px_var(--confirm-shadow)] transition-opacity active:opacity-75"
          >
            {t.chatStartAgain}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Order card ─────────────────────────────────────────────────────────────

const OrderCard = ({ suggestion, onOrder }: { suggestion: OrderSuggestion; onOrder: () => void }) => {
  const isMix = suggestion.kind === 'mix';
  const entries = isMix ? Object.entries(suggestion.mix).filter(([, ml]) => ml > 0) : [];
  const total = entries.reduce((s, [, ml]) => s + ml, 0);

  return (
    <div className="ml-11 [animation:pageFadeIn_0.35s_ease_both]">
      <div className="rounded-[18px] border border-[var(--gold-lt)] bg-[var(--card)] p-4 shadow-[0_0_18px_var(--confirm-shadow)]">
        <p className="mb-1 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-[var(--gold-lt)]">
          {isMix ? t.chatCustomBlend : t.chatRecommendedDrink}
        </p>
        <p className="mb-3 font-display text-[1rem] font-bold text-[var(--cream)]">
          {suggestion.name}
        </p>

        {isMix && entries.length > 0 && (
          <div className="mb-4 flex flex-col gap-1.5">
            {entries.map(([id, ml]) => {
              const pct = Math.round((ml / total) * 100);
              return (
                <div key={id} className="flex items-center gap-2">
                  <span className="w-[7rem] shrink-0 truncate text-[0.78rem] text-[var(--muted-lt)]">
                    {suggestion.kind === 'mix' ? (suggestion.ingredient_names?.[id] ?? id) : id}
                  </span>
                  <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-[rgba(255,255,255,0.08)]">
                    <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--gold-lt)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right text-[0.78rem] tabular-nums text-[var(--muted)]">{ml}ml</span>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={onOrder}
          className="touch-target w-full rounded-[12px] border border-[var(--confirm-border)] bg-[var(--confirm-bg)] py-3.5 font-display text-[0.95rem] font-bold uppercase tracking-[0.12em] text-white shadow-[0_0_14px_var(--confirm-shadow)] transition-opacity active:opacity-75"
        >
          {t.chatLetsOrder}
        </button>
      </div>
    </div>
  );
};

// ── Shared sub-components ──────────────────────────────────────────────────

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex items-end gap-2 [animation:pageFadeIn_0.3s_ease_both] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && <Avatar />}
      <div
        className={`max-w-[78%] whitespace-pre-wrap break-words rounded-[20px] px-4 py-2.5 text-[0.95rem] leading-snug ${
          isUser
            ? 'rounded-br-[6px] border border-[var(--confirm-border)] bg-[var(--accent-pressed-bg)] text-[var(--cream)] shadow-[0_0_18px_var(--accent-pressed-shadow)]'
            : 'rounded-bl-[6px] border border-[var(--border)] bg-[var(--card)] text-[var(--cream)] shadow-[var(--card-shadow)]'
        }`}
      >
        {message.text}
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex items-end gap-2">
    <Avatar />
    <div className="flex items-center gap-1.5 rounded-[20px] rounded-bl-[6px] border border-[var(--border)] bg-[var(--card)] px-4 py-3.5 shadow-[var(--card-shadow)]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="block h-2 w-2 rounded-full bg-[var(--gold-lt)] [animation:typingDot_1.2s_ease-in-out_infinite]"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>
  </div>
);

const Avatar = () => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card-2)] text-[1.1rem] [filter:drop-shadow(0_2px_10px_var(--accent-glow))]">
    💬
  </div>
);

export default ChatPage;
